/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { Input } from 'reactstrap';
import ValueSlider from './ValueSlider';
import { renderInputGroup } from './InputGroup';
import { InputGroupPrePend } from './InputGroupPrePend';
import { BaseInputProps } from './InputProps';
import { InputHelperText } from './InputHelperText';
import { getUtrDecimal, getUtrNumberValue } from '@utils/universalTracker';
import { getAddonSingleElement } from './addonUtils';
import { usePopulateInputFromConnection } from '@features/assistant';
import classnames from 'classnames';
import { isDefined } from '@utils/index';
import { getInputWarningMessage } from '@utils/survey/input';
import { useVariationContext } from '@features/survey/question/variation/VariationContext';

export default function NumberInput(props: BaseInputProps) {
  const {
    handleFocus,
    handleValueChange,
    handleUnitChange,
    universalTracker,
    questionValue,
    isDisabled,
    placeholder,
    inputMessage,
    hasValueChanged,
    addons,
    unit,
    numberScale,
    suffix,
    handleNumberScaleChange,
    initiativeUtr,
  } = props;

  const disabled = Boolean(isDisabled?.(props));
  const value = questionValue ?? '';
  const utrId = universalTracker.getId();
  const invalidMessage = inputMessage?.[utrId];
  const isInvalid = !!invalidMessage;

  const min = universalTracker.getMin();
  const max = universalTracker.getMax();

  const decimal = getUtrDecimal(universalTracker);
  const valueWithDecimal = getUtrNumberValue({ value, decimal, hasValueChanged });
  const { beforeElement, afterElement } = getAddonSingleElement(addons);
  const unitWarningMessage = getInputWarningMessage({ utr: universalTracker, unit, numberScale, initiativeUtr });
  const { getVariationWarningMessage } = useVariationContext();
  const warningMessage = unitWarningMessage || getVariationWarningMessage();
  usePopulateInputFromConnection({
    inputChangeHandler: handleValueChange,
  });

  if (universalTracker.isPercentage() || universalTracker.hasMinAndMax()) {
    return (
      <ValueSlider
        value={valueWithDecimal}
        universalTracker={universalTracker}
        invalidMessage={invalidMessage}
        disabled={disabled}
        placeholder={placeholder}
        handleChange={handleValueChange}
        min={min}
        max={max}
        beforeAddon={beforeElement}
        afterAddon={afterElement}
        suffix={suffix}
        numberScale={numberScale}
        unit={unit}
        handleNumberScaleChange={handleNumberScaleChange}
        status={props.status}
        initiativeUtr={props.initiativeUtr}
        warningMessage={warningMessage}
      />
    );
  }

  return (
    <div
      className={classnames('position-relative input-group mb-3', {
        'has-validation': isDefined(decimal) || !!warningMessage,
      })}
    >
      {beforeElement || null}
      {InputGroupPrePend({ ...props, isInvalid })}
      <Input
        type='number'
        placeholder={placeholder}
        autoComplete='off'
        className={isInvalid ? '' : 'styled-input'}
        name={utrId}
        value={valueWithDecimal}
        aria-describedby='suffix'
        disabled={disabled}
        required={true}
        onFocus={handleFocus}
        onClick={handleFocus}
        invalid={isInvalid}
        onWheel={(e) => e.currentTarget.blur()}
        onChange={(e) => handleValueChange({ value: e.target.value, min, max })}
      />
      {renderInputGroup({
        ...props,
        isDisabled,
        isInvalid,
        handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
          handleUnitChange(e.target.value),
      })}
      {disabled ? null : (
        <InputHelperText
          invalidMessage={invalidMessage}
          universalTracker={universalTracker}
          warningMessage={warningMessage}
        />
      )}
      {afterElement || null}
    </div>
  );
}
